import React, { useState, useEffect, useRef } from 'react'
import Doodle2 from '../assets/images/doodle2.jpg'
import { handleWheel, handleMouseEnter, handleMouseLeave } from '../utils/Scroll';


const WhatsappChat = () => {
  const [users, setUsers] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hasMoreMessages, setHasMoreMessages] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [expandedMessages, setExpandedMessages] = useState(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredUsers, setFilteredUsers] = useState([])
  const messagesEndRef = useRef(null)
  const messagesContainerRef = useRef(null)
  const [page, setPage] = useState(1)

  // API base URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_HOST_API_URL

  // Fetch users list
  const fetchUsers = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      const response = await fetch(`${API_BASE_URL}/admin-user/whatsapp-chat/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch users')
      }

      const data = await response.json()
      if (data.success) {
        setUsers(data.data)
      }
    } catch (err) {
      setError('Failed to load users: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  // Fetch messages for selected user
  const fetchMessages = async (customerId, pageNum = 1, loadMore = false) => {
    try {
      if (loadMore) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }

      const token = localStorage.getItem("token")
      const response = await fetch(`${API_BASE_URL}/admin-user/whatsapp-chat/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_id: customerId,
          page: pageNum,
          limit: 20
        })
      })

      if (!response.ok) {
        throw new Error('Failed to fetch messages')
      }

      const data = await response.json()
      if (data.success) {
        if (loadMore) {
          setMessages(prev => [...data.data, ...prev])
        } else {
          setMessages(data.data)
        }
        setHasMoreMessages(data.data.length === 20)
      }
    } catch (err) {
      setError('Failed to load messages: ' + err.message)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  // Handle user selection
  const handleUserSelect = (user) => {
    setSelectedUser(user)
    setPage(1)
    setHasMoreMessages(true)
    fetchMessages(user.id)
  }

  // Handle sending new message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedUser) return

    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      const response = await fetch(`${API_BASE_URL}/adminstrator_user/whatsapp-chat/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_id: selectedUser.id,
          message_text: newMessage,
          message_type: 'text',
          is_incoming: false
        })
      })

      if (!response.ok) {
        throw new Error('Failed to send message')
      }

      const data = await response.json()
      if (data.success) {
        // Refresh messages after sending
        fetchMessages(selectedUser.id)
        setNewMessage('')
      }
    } catch (err) {
      setError('Failed to send message: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  // Handle scroll to load more messages
  const handleScroll = (e) => {
    if (e.target.scrollTop === 0 && hasMoreMessages && !loadingMore) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchMessages(selectedUser.id, nextPage, true)
    }
  }

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())

    if (messageDate.getTime() === today.getTime()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } else {
      return date.toLocaleDateString()
    }
  }

  // Format time for message display
  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  // Check if message is from today
  const isToday = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    return date.toDateString() === now.toDateString()
  }

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Load users on component mount
  useEffect(() => {
    fetchUsers()
  }, [])

  // Initialize filtered users when users are loaded
  useEffect(() => {
    setFilteredUsers(users)
  }, [users])

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  // Toggle message expansion
  const toggleMessageExpansion = (messageId) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev)
      if (newSet.has(messageId)) newSet.delete(messageId)
      else newSet.add(messageId)
      return newSet
    })
  }

  // Check if message should be truncated
  const shouldTruncate = (messageText) => {
    return messageText.length > 200
  }

  // Get truncated message text
  const getTruncatedText = (messageText) => {
    return messageText.substring(0, 200) + '...'
  }

  // Handle search input
  const handleSearchChange = (e) => {
    const searchValue = e.target.value.toLowerCase()
    setSearchTerm(searchValue)

    if (searchValue.trim() === '') {
      setFilteredUsers(users)
    } else {
      const filtered = users.filter(user =>
        user.profile_name.toLowerCase().includes(searchValue) ||
        (user.mobile && user.mobile.toLowerCase().includes(searchValue)) ||
        (user.preferred_email && user.preferred_email.toLowerCase().includes(searchValue))
      )
      setFilteredUsers(filtered)
    }
  }

  // Get users to display (filtered or all)
  const getDisplayUsers = () => {
    return searchTerm.trim() === '' ? users : filteredUsers
  }

  // Generate random color for profile picture
  const getRandomColor = (name) => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
      '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
      '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2'
    ]
   if (!name) return '#ccc'; 
    const hash = name.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0)
      return a & a
    }, 0)
    return colors[Math.abs(hash) % colors.length]
  }


  // Get first letter of name
  const getFirstLetter = (name) => {
    return name ? name.charAt(0).toUpperCase() : '?'
  }

  return (
    <div className="container-fluid p-0 mt-0" style={{ height: "100vh", overflow: 'hidden', }}>
      <div className="row g-0" style={{ height: '100%' }}>
        {/* Left Sidebar - Users List */}
        <div className="col-md-4 col-lg-3 bg-light border-end"
          style={{
            height: '100%',
            width: "30%",
            overflowY: "auto",
            flexShrink: 0,
          }}
          onWheel={handleWheel}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          <div className="d-flex flex-column" style={{ height: '100vh' }}>
            {/* Header */}
            <div className=" text-white p-3" style={{ backgroundColor: ' #318CE7' }}>
              <h5 className="mb-0">AI Chats</h5>
            </div>

            {/* Search Bar */}
            <div className="p-3 border-bottom">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name, mobile, or email..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <button className="btn btn-outline-secondary" type="button">
                  <i className="fas fa-search"></i>
                </button>
              </div>
            </div>

            {/* Users List */}
            <div className="overflow-auto"
            // style={{ height: 'calc(80vh - 120px)' }}
            >
              {loading ? (
                <div className="text-center p-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : getDisplayUsers().length > 0 ? (
                getDisplayUsers().map((user) => (
                  <div
                    key={user.id}
                    onClick={() => handleUserSelect(user)}
                    className={`p-3 border-bottom cursor-pointer user-item ${selectedUser?.id === user.id ? 'bg-primary text-white' : 'bg-white'
                      }`}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        <div className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                          style={{
                            width: '50px',
                            height: '50px',
                            backgroundColor: getRandomColor(user.profile_name),
                            fontSize: '18px'
                          }}>
                          {getFirstLetter(user.profile_name)}
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="mb-1">{user.profile_name}</h6>
                        <p className="mb-1 text-muted small">
                          {user.today_message_count > 0 && (
                            <span className="badge bg-primary me-1">
                              {user.today_message_count} today
                            </span>
                          )}
                          {user.preferred_email}
                        </p>
                        <small className="text-muted">
                          {/* {formatDate(user.created_at)} */}
                          {user.mobile}

                        </small>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center p-4">
                  <i className="fas fa-search fa-2x text-muted mb-2"></i>
                  <p className="text-muted">No users found</p>
                  <small className="text-muted">Try searching by name, mobile, or email</small>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Chat Messages */}
        <div className="d-flex flex-column h-100" style={{ width: '70%' }}
          onWheel={handleWheel}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          {selectedUser ? (
            <>
              {/* Chat Header */}
              <div className=" bg-light border-bottom p-3">
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                      style={{
                        width: '40px',
                        height: '40px',
                        backgroundColor: getRandomColor(selectedUser.profile_name),
                        fontSize: '16px'
                      }}>
                      {getFirstLetter(selectedUser.profile_name)}
                    </div>
                  </div>
                  <div>
                    <h6 className="mb-0">{selectedUser.profile_name}</h6>
                  </div>
                </div>
              </div>

              {/* Messages Area */}
              <div
                className="flex-grow-1 overflow-auto p-3"
                ref={messagesContainerRef}
                onScroll={handleScroll}
                style={{
                  height: 'calc(100vh - 140px)',
                  backgroundImage: `url(${Doodle2})`,
                  backgroundSize: 'contain',

                }}
              >
                {loadingMore && (
                  <div className="text-center mb-3">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading more messages...</span>
                    </div>
                  </div>
                )}

                {messages.map((message, index) => {
                  const isIncoming = message.is_incoming === 'incoming' || message.is_incoming === true
                  console.log('isIncoming:', isIncoming)
                  const showDate = index === 0 ||
                    new Date(message.created_at).toDateString() !==
                    new Date(messages[index - 1].created_at).toDateString()
                  const messageKey = message.whatsapp_message_id || index
                  const isExpanded = expandedMessages.has(messageKey)

                  return (
                    <div key={messageKey}>
                      {showDate && (
                        <div className="text-center my-3">
                          {/* <span className="badge bg-secondary">
                            {isToday(message.created_at) ? 'Today' : formatDate(message.created_at)}
                          </span> */}
                          <span className="badge bg-secondary">{isToday(message.created_at) ? 'Today' : formatDate(message.created_at)}</span>

                        </div>
                      )}

                      <div className={`d-flex mb-3 ${isIncoming ? 'justify-content-start' : 'justify-content-end'}`}>
                        <div className={`message-bubble ${isIncoming ? 'incoming' : 'outgoing'}`}>
                          <div className="message-content">
                            {shouldTruncate(message.message_text) && !isExpanded ? (
                              <>
                                {getTruncatedText(message.message_text)}
                                <button
                                  className="btn btn-link p-0 text-decoration-none read-more-btn"
                                  onClick={() => toggleMessageExpansion(messageKey)}
                                  style={{ fontSize: '12px', color: '#6c757d' }}
                                >
                                  Read more
                                </button>
                              </>
                            ) : (
                              <>
                                {message.message_text}
                                {shouldTruncate(message.message_text) && (
                                  <button
                                    className="btn btn-link p-0 text-decoration-none read-more-btn"
                                    onClick={() => toggleMessageExpansion(messageKey)}
                                    style={{ fontSize: '12px', color: '#6c757d' }}
                                  >
                                    Show less
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                          <div className={`message-time ${isIncoming ? 'text-start' : 'text-end'}`}>
                            {formatTime(message.created_at)}
                            {!isIncoming && (
                              <span className="ms-1">
                                {message.whatsapp_message_status === 'delivered' ? '✓✓' : '✓'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}

                {loading && (
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading messages...</span>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="border-top p-3 bg-white">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                  />
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || loading}
                  >
                    <i className="fas fa-paper-plane"></i>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="d-flex align-items-center justify-content-center" style={{ height: 'calc(80vh - 60px)' }}>
              <div className="text-center">
                <i className="fas fa-comments fa-3x text-muted mb-3"></i>
                <h5 className="text-muted">Select a user to start chatting</h5>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show position-fixed"
          style={{ top: '20px', right: '20px', zIndex: 1050 }}>
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      <style jsx>{`
        .user-item:hover {
          background-color: #f8f9fa !important;
        }
        
        .user-item.bg-primary:hover {
          background-color: #318CE7 !important;
        }
        
        .message-bubble {
          max-width: 70%;
          padding: 10px 15px;
          border-radius: 8px;
          position: relative;
          word-wrap: break-word;
          box-shadow: 0 1px 2px rgba(0,0,0,0.1);
        }
        
        .message-bubble.incoming {
          background-color: #FBAED2;
          border: 1px solid #e9ecef;
          border-radius: 8px 8px 8px 2px;
        }
        
        .message-bubble.outgoing {
          background-color: #318CE7;
          color: white;
          border: 1px solid #0056b3;
          border-radius: 8px 8px 2px 8px;
        }
        
        .message-content {
          margin-bottom: 5px;
          word-wrap: break-word;
          font-size: 14px;
          line-height: 1.4;
        }
        
        .message-time {
          font-size: 0.7rem;
          color: #6c757d;
          opacity: 0.8;
        }
        
        .message-time.text-end {
          text-align: right;
        }
        
        .message-time.text-start {
          text-align: left;
        }
        
        .cursor-pointer {
          cursor: pointer;
        }
        
        .read-more-btn {
          font-size: 12px !important;
          color: #6c757d !important;
          text-decoration: none !important;
          padding: 0 !important;
          margin-left: 5px;
          cursor: pointer;
        }
        
        .read-more-btn:hover {
          color: #0d6efd !important;
          text-decoration: underline !important;
        }
      `}</style>

    </div>
  )
}

export default WhatsappChat
